import React, { useState, useEffect } from "react";
import {
  DollarSign,
  Search,
  Calendar,
  CheckCircle,
  XCircle,
  Users,
  Building2,
  Filter,
  Check,
  X,
} from "lucide-react";
import apiService from "../services/api";
import "../styles/SalaryPay.css";

const SalaryPay = () => {
  const [salaries, setSalaries] = useState([]);
  const [filteredSalaries, setFilteredSalaries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");

  // Filters
  const [searchName, setSearchName] = useState("");
  const [filterSite, setFilterSite] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterPaid, setFilterPaid] = useState("");

  // Get unique values for filters
  const uniqueSites = [
    ...new Set(salaries.map((s) => s.site_name).filter(Boolean)),
  ].sort();
  const uniqueCustomers = [
    ...new Set(salaries.map((s) => s.customer_name).filter(Boolean)),
  ].sort();
  const uniqueTypes = [
    ...new Set(salaries.map((s) => s.employee_type).filter(Boolean)),
  ].sort();

  useEffect(() => {
    loadAvailableMonths();
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      loadSalaries();
    }
  }, [selectedMonth]);

  useEffect(() => {
    applyFilters();
  }, [
    salaries,
    searchName,
    filterSite,
    filterCustomer,
    filterType,
    filterPaid,
  ]);

  const loadAvailableMonths = async () => {
    try {
      const response = await apiService.getSalaryPayMonths();
      if (response.success && response.months.length > 0) {
        setAvailableMonths(response.months);
        setSelectedMonth(response.months[0]);
      } else {
        const currentMonth = new Date().toISOString().slice(0, 7);
        setSelectedMonth(currentMonth);
      }
    } catch (error) {
      console.error("Failed to load months:", error);
      const currentMonth = new Date().toISOString().slice(0, 7);
      setSelectedMonth(currentMonth);
    }
  };

  const loadSalaries = async () => {
    try {
      setLoading(true);
      const response = await apiService.getSalariesToPay(selectedMonth);
      if (response.success) {
        setSalaries(response.data || []);
        setSummary(response.summary || null);
      }
    } catch (error) {
      console.error("Failed to load salaries:", error);
      alert("שגיאה בטעינת נתוני משכורות");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...salaries];

    if (searchName) {
      filtered = filtered.filter((s) =>
        s.employee_name?.toLowerCase().includes(searchName.toLowerCase())
      );
    }

    if (filterSite) {
      filtered = filtered.filter((s) => s.site_name === filterSite);
    }

    if (filterCustomer) {
      filtered = filtered.filter((s) => s.customer_name === filterCustomer);
    }

    if (filterType) {
      filtered = filtered.filter((s) => s.employee_type === filterType);
    }

    if (filterPaid === "paid") {
      filtered = filtered.filter((s) => s.paid);
    } else if (filterPaid === "unpaid") {
      filtered = filtered.filter((s) => !s.paid);
    }

    setFilteredSalaries(filtered);
  };

  const handleTogglePaid = async (salaryId) => {
    try {
      const response = await apiService.toggleSalaryPaid(salaryId);
      if (response.success) {
        // Update local state
        setSalaries((prev) =>
          prev.map((s) =>
            s.id === salaryId ? { ...s, paid: response.paid } : s
          )
        );
        // Reload to update summary
        loadSalaries();
      }
    } catch (error) {
      console.error("Failed to toggle paid status:", error);
      alert("שגיאה בעדכון סטטוס תשלום");
    }
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return "";
    const [year, month] = monthStr.split("-");
    const monthNames = [
      "ינואר",
      "פברואר",
      "מרץ",
      "אפריל",
      "מאי",
      "יוני",
      "יולי",
      "אוגוסט",
      "ספטמבר",
      "אוקטובר",
      "נובמבר",
      "דצמבר",
    ];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("he-IL", {
      style: "currency",
      currency: "ILS",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const clearFilters = () => {
    setSearchName("");
    setFilterSite("");
    setFilterCustomer("");
    setFilterType("");
    setFilterPaid("");
  };

  return (
    <div className='salary-pay-container'>
      <div className='salary-pay-header'>
        <div className='header-content'>
          <h1>
            <DollarSign className='header-icon' />
            תשלום משכורות
          </h1>
          <p className='header-subtitle'>ניהול ומעקב תשלומי משכורות לעובדים</p>
        </div>

        <div className='header-actions'>
          <div className='month-selector'>
            <Calendar size={18} />
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <option value=''>בחר חודש</option>
              {availableMonths.map((month) => (
                <option key={month} value={month}>
                  {formatMonth(month)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className='summary-cards'>
          <div className='summary-card total'>
            <div className='card-icon'>
              <Users size={24} />
            </div>
            <div className='card-content'>
              <p className='card-label'>סה״כ עובדים</p>
              <h3 className='card-value'>{summary.total_count}</h3>
            </div>
          </div>

          <div className='summary-card paid'>
            <div className='card-icon'>
              <CheckCircle size={24} />
            </div>
            <div className='card-content'>
              <p className='card-label'>שולמו</p>
              <h3 className='card-value'>{summary.paid_count}</h3>
              <p className='card-amount'>
                {formatCurrency(summary.total_paid)}
              </p>
            </div>
          </div>

          <div className='summary-card unpaid'>
            <div className='card-icon'>
              <XCircle size={24} />
            </div>
            <div className='card-content'>
              <p className='card-label'>לא שולמו</p>
              <h3 className='card-value'>{summary.unpaid_count}</h3>
              <p className='card-amount'>
                {formatCurrency(summary.total_unpaid)}
              </p>
            </div>
          </div>

          <div className='summary-card amount'>
            <div className='card-icon'>
              <DollarSign size={24} />
            </div>
            <div className='card-content'>
              <p className='card-label'>סה״כ לתשלום</p>
              <h3 className='card-value'>
                {formatCurrency(summary.total_to_pay)}
              </h3>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className='filters-section'>
        <div className='filters-header'>
          <Filter size={18} />
          <span>סינון</span>
          <button className='clear-filters-btn' onClick={clearFilters}>
            נקה הכל
          </button>
        </div>

        <div className='filters-row'>
          <div className='filter-item search'>
            <Search size={18} />
            <input
              type='text'
              placeholder='חיפוש לפי שם עובד...'
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />
          </div>

          <div className='filter-item'>
            <select
              value={filterCustomer}
              onChange={(e) => setFilterCustomer(e.target.value)}
            >
              <option value=''>כל הלקוחות</option>
              {uniqueCustomers.map((customer) => (
                <option key={customer} value={customer}>
                  {customer}
                </option>
              ))}
            </select>
          </div>

          <div className='filter-item'>
            <select
              value={filterSite}
              onChange={(e) => setFilterSite(e.target.value)}
            >
              <option value=''>כל האתרים</option>
              {uniqueSites.map((site) => (
                <option key={site} value={site}>
                  {site}
                </option>
              ))}
            </select>
          </div>

          <div className='filter-item'>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value=''>כל הסוגים</option>
              {uniqueTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className='filter-item'>
            <select
              value={filterPaid}
              onChange={(e) => setFilterPaid(e.target.value)}
            >
              <option value=''>הכל</option>
              <option value='paid'>שולמו</option>
              <option value='unpaid'>לא שולמו</option>
            </select>
          </div>
        </div>

        <div className='filter-results'>
          מציג {filteredSalaries.length} מתוך {salaries.length} עובדים
        </div>
      </div>

      {/* Salaries Table */}
      <div className='salaries-table-container'>
        {loading ? (
          <div className='loading-state'>
            <div className='spinner'></div>
            <p>טוען נתונים...</p>
          </div>
        ) : filteredSalaries.length === 0 ? (
          <div className='empty-state'>
            <DollarSign size={48} />
            <h3>אין משכורות</h3>
            <p>לא נמצאו משכורות לחודש {formatMonth(selectedMonth)}</p>
          </div>
        ) : (
          <table className='salaries-table'>
            <thead>
              <tr>
                <th>שם עובד</th>
                <th>לקוח</th>
                <th>אתר</th>
                <th>סוג</th>
                <th>לתשלום</th>
                <th>סטטוס</th>
                <th>פעולה</th>
              </tr>
            </thead>
            <tbody>
              {filteredSalaries.map((salary) => (
                <tr
                  key={salary.id}
                  className={salary.paid ? "paid-row" : "unpaid-row"}
                >
                  <td className='employee-name'>{salary.employee_name}</td>
                  <td>{salary.customer_name || "-"}</td>
                  <td>{salary.site_name || "-"}</td>
                  <td>
                    <span
                      className={`type-badge ${salary.employee_type?.replace(
                        /\s/g,
                        "-"
                      )}`}
                    >
                      {salary.employee_type || "-"}
                    </span>
                  </td>
                  <td className='amount-cell'>
                    {formatCurrency(salary.to_pay)}
                  </td>
                  <td>
                    <span
                      className={`status-badge ${
                        salary.paid ? "paid" : "unpaid"
                      }`}
                    >
                      {salary.paid ? "שולם" : "לא שולם"}
                    </span>
                  </td>
                  <td>
                    <button
                      className={`toggle-btn ${
                        salary.paid ? "mark-unpaid" : "mark-paid"
                      }`}
                      onClick={() => handleTogglePaid(salary.id)}
                      title={salary.paid ? "סמן כלא שולם" : "סמן כשולם"}
                    >
                      {salary.paid ? <X size={16} /> : <Check size={16} />}
                      {salary.paid ? "בטל תשלום" : "סמן כשולם"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default SalaryPay;
